type StatusLabel = { label: string; tone: "ok" | "warn" | "err" };

/**
 * TikTok reports an advertiser's status as a string enum. Every value on TikTok's
 * "Enumeration - Advertiser Status" list is named here, its description shortened
 * into a label: https://business-api.tiktok.com/portal/docs?id=1737174886619138
 *
 * Only `STATUS_ENABLE` ("Approved") is healthy. Everything pending is a warning,
 * everything refused, closed or punished is an error.
 *
 * A Map rather than an object literal: the key is text read off the wire, and a
 * status of `constructor` must not find a function on Object.prototype.
 */
export const ADVERTISER_STATUS: ReadonlyMap<string, StatusLabel> = new Map([
	["STATUS_ENABLE", { label: "Approved", tone: "ok" }],
	["STATUS_DISABLE", { label: "Closed", tone: "err" }],
	["STATUS_CONFIRM_FAIL", { label: "Review failed", tone: "err" }],
	["STATUS_CONFIRM_FAIL_END", { label: "CRM review failed", tone: "err" }],
	["STATUS_LIMIT", { label: "Under punishment", tone: "err" }],
	["STATUS_PENDING_CONFIRM", { label: "Pending review", tone: "warn" }],
	["STATUS_PENDING_VERIFIED", { label: "Pending verification", tone: "warn" }],
	["STATUS_PENDING_CONFIRM_MODIFY", { label: "Changes pending review", tone: "warn" }],
	["STATUS_CONFIRM_MODIFY_FAIL", { label: "Changes failed review", tone: "warn" }],
	["STATUS_WAIT_FOR_BPM_AUDIT", { label: "Pending CRM review", tone: "warn" }],
	["STATUS_WAIT_FOR_PUBLIC_AUTH", { label: "Pending bank account check", tone: "warn" }],
	["STATUS_SELF_SERVICE_UNAUDITED", { label: "Qualifications unverified", tone: "warn" }],
	["STATUS_CONTRACT_PENDING", { label: "Contract not in effect", tone: "warn" }],
] as const);

export function accountStatus(status: string | null): StatusLabel {
	return ADVERTISER_STATUS.get(status ?? "") ?? { label: "Unknown", tone: "warn" };
}

/**
 * Where the status beside it came from, and when.
 *
 * 🔴 The status an account card shows is the mirror's — the row that
 * `POST /tiktok-accounts/sync` last wrote, refreshed by an explicit sync and never
 * on a page load (`docs/API-CONTRACT.md`, GET /api/v1/tiktok-accounts) — and not
 * TikTok's answer of this minute. A badge with no date is read as the present: in
 * twads the card said REJECTED off a sync twelve days old while X had already
 * cleared the account (twads#155). Here no sync has run yet, so today every card
 * says so in words rather than showing «Unknown» over a row nothing has written.
 * The sentence names the mirror, carries the sync's time as the Ad accounts table
 * does, and says where a fresh read is. `format` is the date's rendering,
 * injectable so a case can pin the sentence without pinning a locale.
 */
export function syncedLabel(
	syncedAt: string | null,
	format: (iso: string) => string = (iso) => new Date(iso).toLocaleString(),
): string {
	if (syncedAt === null)
		return "Status from the mirror, never synced: TikTok has not been asked about this account yet.";
	return `Status from the mirror, synced ${format(syncedAt)}. TikTok may have changed since — Sync from TikTok on Ad accounts reads it again.`;
}

/** Prefer a human handle, but never render an empty string as the identity. */
export function displayName(me: { email?: string; id?: string } | null): string {
	const email = me?.email?.trim();
	if (email) return email;
	const id = me?.id?.trim();
	if (id) return id;
	return "your G1 account";
}
